/* globals alertify */

angular.module('zc.zenclerkS')
  .factory('Devise', Devise);

Devise.$inject = ['$http', '$injector', '$location', '$q', '$translate', '$window', 'Api', 'returnUrl'];
function Devise($http, $injector, $location, $q, $translate, $window, Api, returnUrl) {
  const service = {
    currentUser: undefined,
    signIn,
    signOut,
    signUp,
    update,
    updateInfo,
    sendPasswordResetInstructions,
    resetPassword,
    updatePassword,
    validateInvitationToken,
    accept,
    getCurrentUser,
    getCurrentUserLanguage,
    isAuthenticated,
    isMaster,
    signAgreements,
  };

  return service;

  function signIn(email, password, rememberMe) {
    return Api.post('/users/sign_in', {
      user: {
        email,
        password,
        rememberMe
      }
    }
    )
      .then(res => {
        if(!res.data.success) {
          alertify.error($translate.instant('services.devise.incorrectLogin'), 0);
          $q.reject(res);
          return;
        }
        $http.defaults.headers.common['X-CSRF-TOKEN'] = res.data.csrfToken;
        service.currentUser = res.data.user;
        return service.currentUser;
      });
  }

  function signOut() {
    if($window.ZC_CONFIG.ENABLE_AUTH0) {
      service.currentUser = null;
      $window.location.href = `${Api.basePath}/users/auth/logout?origin=${$window.location.origin}`;
    } else {
      Api.delete('/users/sign_out')
        .then(res => {
          $http.defaults.headers.common['X-CSRF-TOKEN'] = res.data.csrfToken;
          service.currentUser = null;
          return $window.location.replace('/users/sign_in');
        });
    }
  }

  function signUp(companyName, userFullname, userEmail, userPassword, userPasswordConfirmation) {
    return Api.post('/users', {
      company: {
        name: companyName
      },
      user: {
        full_name: userFullname,
        email: userEmail,
        password: userPassword,
        password_confirmation: userPasswordConfirmation
      }
    }
    )
      .then(res => {
        service.currentUser = res.data.user;
        return service.currentUser;
      });
  }

  function update(companyName, userFullname, userEmail) {
    return Api.patch('/users', {
      company: {
        name: companyName
      },
      user: {
        full_name: userFullname,
        email: userEmail
      }
    }
    )
      .then(res => {
        service.currentUser = res.data.user;
        return service.currentUser;
      });
  }

  function updateInfo(user) {
    return Api.patch('/api/users/update_info',
      {user})
      .then(res => {
      // Ideally would like to do _.merge, but for some reason, after first
      // login, we get login_token null response, which is bad.
      // (although currentUser already has the proper login_token from sign_in)
      // Does not happen locally but only in staging!! (and likely in
      // production)
        const safeData = _.omit(res.data.user, ['login_token']);
        service.currentUser = _.merge(service.currentUser, safeData);
        return service.currentUser;
      });
  }

  function sendPasswordResetInstructions(email) {
    // workaround to show loading spinner without wait
    const NotificationChannel = $injector.get('NotificationChannel');
    NotificationChannel.requestStarted();
    return Api.post('/users/password', {
      zcs: true,
      user: {
        email
      }
    }
    )
      .then(res => {
        service.currentUser = res.data.user;
        return service.currentUser;
      });
  }

  function resetPassword(resetPasswordToken, password, passwordConfirmation) {
    return Api.put('/users/password', {
      user: {
        password,
        password_confirmation: passwordConfirmation,
        reset_password_token: resetPasswordToken
      }
    }
    )
      .then(res => {
        service.currentUser = res.data.user;
        return service.currentUser;
      });
  }

  function updatePassword(currentPassword, password, passwordConfirmation) {
    // workaround to show loading spinner without wait
    const NotificationChannel = $injector.get('NotificationChannel');
    NotificationChannel.requestStarted();
    return Api.patch('/api/users/update_password', {
      user: {
        password,
        current_password: currentPassword,
        password_confirmation: passwordConfirmation
      }
    }
    )
      .then(res => {
        service.currentUser = res.data.user;
        return service.currentUser;
      })
      .catch(res => {
        const errCnt = _.reduce(res.data.errors, (cnt, errors) => cnt + errors.length
          , 0);
        // TODO: Move to directives
        if(errCnt === 0) { alertify.error($translate.instant('services.devise.incorrectCurrentPassword')) }
        return res;
      });
  }

  function validateInvitationToken(invitationToken) {
    return Api.get(`/users/invitation/accept?invitation_token=${invitationToken}`)
      .then(res => {
        if(res.data) { res.data.user.invitation_token = invitationToken }
        return _.get(res.data, 'user');
      });
  }

  function accept(user) {
    return Api.patch('/users/invitation', {
      user: {
        full_name: user.full_name,
        email: user.email,
        password: user.password,
        password_confirmation: user.password_confirmation,
        invitation_token: user.invitation_token,
        lang: user.lang,
      },
      locale: user.lang,
    }).then(() => service.signIn(user.email, user.password, true));
  }

  function getCurrentUser(opts = {}) {
    if(service.isAuthenticated() && !opts.force) {
      return $q.when(service.currentUser);
    }

    let apiParams = {};
    if(opts.force) {
      apiParams = { isPreflight: opts.isPreflight, cache: false };
    } else {
      apiParams = { isPreflight: opts.isPreflight};
    }

    return Api.get('/users/show_current', {}, apiParams)
      // load language first
      .then(res => {
        if(res.data.test === 'test') {
          return res;
        }
        if(!res.data.success) return res;
        return $translate.use(res.data.user.lang).then(() => res);
      })
      .then(res => {
        if(!res.data.success) {
          if($location.path() !== '/users/sign_in' && $location.path() !== '/users/invitation/accept') {
            returnUrl.set($location.path());
          }
          return $q.reject(null);
        }
        $translate.use(res.data.user.lang);

        $http.defaults.headers.common['X-CSRF-TOKEN'] = res.data.csrfToken;
        service.currentUser = res.data.user;
        return res.data.user;
      })
      .catch(err => $q.reject(null));
  }

  function getCurrentUserLanguage() {
    if(service.isAuthenticated()) {
      return $q.when(service.currentUser.lang);
    }

    return Api.get('/users/show_current', {}).then(function(res) {
      return _.get(res, 'data.user.lang');
    });
  }

  function isAuthenticated() {
    return !!service.currentUser;
  }

  function isMaster() {
    return _.get(service.currentUser, 'role') === 'master';
  }

  function signAgreements(user) {
    return Api.patch(`/api/users/${user.id}/sign_agreements`)
      .then(res => {
      // Ideally would like to do _.merge, but for some reason, after first
      // login, we get login_token null response, which is bad.
      // (although currentUser already has the proper login_token from sign_in)
      // Does not happen locally but only in staging!! (and likely in
      // production)
        const safeData = _.omit(res.data.user, ['login_token']);
        service.currentUser = _.merge(service.currentUser, safeData);
        return service.currentUser;
      });
  }
}



// WEBPACK FOOTER //
// ./src/zenclerk_s_modules/services/devise.service.js